import { useState } from 'react';

import dayjs from 'dayjs';

interface UseAssignmentDateStateParams {
  initialDateFrom?: Date;
  initialDateTo?: Date;
  dateRangeFrom?: Date;
  dateRangeTo?: Date;
}

/**
 * Manages date picker open state and the committed dates (used for the rates query
 * and team members query).
 *
 * Committed dates are only updated when the picker closes (Apply / outside click),
 * so queries fire exactly once per confirmed date selection rather than on every
 * intermediate date change inside the picker.
 */
export function useAssignmentDateState({
  initialDateFrom,
  initialDateTo,
  dateRangeFrom,
  dateRangeTo,
}: UseAssignmentDateStateParams) {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // For create mode, default to today so the rates query fires as soon as user+project are selected.
  const [committedDateFrom, setCommittedDateFrom] = useState<Date | null>(
    initialDateFrom ? dayjs(initialDateFrom).toDate() : new Date()
  );

  const [committedDateTo, setCommittedDateTo] = useState<Date | null>(
    initialDateTo ? dayjs(initialDateTo).toDate() : new Date()
  );

  function handleDatePickerOpenChange(nextOpen: boolean) {
    setIsDatePickerOpen(nextOpen);
    // Commit both dates when the picker closes so queries fire exactly once
    // per confirmed date selection.
    if (!nextOpen) {
      if (dateRangeFrom) {
        setCommittedDateFrom(dateRangeFrom);
      }
      if (dateRangeTo) {
        setCommittedDateTo(dateRangeTo);
      }
    }
  }

  return {
    isDatePickerOpen,
    setIsDatePickerOpen,
    committedDateFrom,
    setCommittedDateFrom,
    committedDateTo,
    setCommittedDateTo,
    handleDatePickerOpenChange,
  };
}
